import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { subscriptionsApi } from './api'
import type { PaidPlanId } from './types'

export function useCurrentTier() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'profile', 'tier'),
    queryFn: subscriptionsApi.getTier,
    select: (data) => (data.subscription_tier ?? 'free') as 'free' | 'basic' | 'standard' | 'premium',
  })
}

export function useBillingSubscription() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'billing', 'subscription'),
    queryFn: subscriptionsApi.getSubscription,
    retry: false,
  })
}

export function useBillingUsage(enabled = true) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'billing', 'usage'),
    queryFn: subscriptionsApi.getUsage,
    retry: false,
    enabled,
    refetchInterval: 10000,
  })
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: (planId: PaidPlanId) => subscriptionsApi.createCheckoutSession(planId),
  })
}

export function useCancelSubscription() {
  const tenantId = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => subscriptionsApi.cancelSubscription(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'billing', 'subscription') })
    },
  })
}

export function useResumeSubscription() {
  const tenantId = useTenant()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => subscriptionsApi.resumeSubscription(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'billing', 'subscription') })
    },
  })
}
